<template>
  <oie-my-pages-template :title="t('articleWatchTitle')" :text="t('articleWatchSubTitle')">
    <oie-accordion-tabs ref="tabs" name="article-watches">
      <oie-accordion-tab
        name="active"
        :display-name="`Aktiv bevakning${filteredActiveWatchesWithProducts.length > 0 ? ` (${filteredActiveWatchesWithProducts.length})` : ''}`"
        :selected="activeTab === 'active'">
        <!-- Filter and Actions Section -->
        <div v-if="currentWatches.length > 0" class="my-article-watches__controls">
          <div class="my-article-watches__filters">
            <div class="filter-group">
              <label for="status-filter">{{ t('statusLabel') }}:</label>
              <select id="status-filter" v-model="currentFilters.status" @change="applyFilters">
                <option value="">
                  {{ tStatic('allStatuses') }}
                </option>
                <option value="in-stock">
                  {{ tStatic('stockAvailableLabel') }}
                </option>
                <option value="few-stock">
                  {{ tStatic('stockFewLabel') }}
                </option>
                <option value="no-stock">
                  {{ tStatic('stockNoneLabel') }}
                </option>
                <option value="is-order-item">
                  {{ tStatic('stockIsOrderItemLabel') }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label for="sort-filter">{{ tStatic('sortBy') }}:</label>
              <select id="sort-filter" v-model="currentFilters.sortBy" @change="applyFilters">
                <option value="dateDesc">
                  {{ tStatic('dateNewestFirst') }}
                </option>
                <option value="dateAsc">
                  {{ tStatic('dateOldestFirst') }}
                </option>
                <option value="nameAsc">
                  {{ tStatic('articleNameAZ') }}
                </option>
                <option value="nameDesc">
                  {{ tStatic('articleNameZA') }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label for="search-filter">{{ tStatic('searchByName') }}:</label>
              <input
                id="search-filter"
                v-model="currentFilters.searchText"
                type="text"
                :placeholder="tStatic('searchPlaceholder')"
                @input="applyFilters">
            </div>
          </div>

          <div v-if="hasMultiSelect" class="my-article-watches__bulk-actions">
            <div class="bulk-selection">
              <oie-base-checkbox
                v-model="selectAll"
                :text="tStatic('selectAll')"
                @change="toggleSelectAll"></oie-base-checkbox>
              <span v-if="selectedItems.length > 0" class="selection-count">
                {{ tStatic('selectedItems').replace('{count}', selectedItems.length) }}
              </span>
            </div>

            <div v-if="selectedItems.length > 0" class="bulk-actions">
              <oie-base-button
                :text="tStatic('deleteSelected')"
                css-classes="button--red"
                @click="showBulkDeleteModal">
                <template #iconPrepend>
                  <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
                </template>
              </oie-base-button>
            </div>
          </div>
        </div>

        <div class="my-article-watches__head" :class="{'my-article-watches__head--with-checkbox': hasMultiSelect}">
          <div v-if="hasMultiSelect" class="my-article-watches__head-checkbox article-watch-column__checkbox"></div>
          <div class="my-article-watches__head-image article-watch-column__image"></div>
          <div class="my-article-watches__head-article article-watch-column__article">
            {{ tStatic('articleNumberLabel') }}
          </div>
          <div class="my-article-watches__head-date article-watch-column__added-date">
            {{ t('addedLabel') }}
          </div>
          <div v-if="isSuperUser" class="my-article-watches__head-sentat article-watch-column__sent-at">
            {{ t('sendAtLabel') }}
          </div>
          <div class="my-article-watches__head-price article-watch-column__price">
            {{ t('pricePerArticle') }}
          </div>
          <div class="my-article-watches__head-status article-watch-column__status">
            {{ t('statusLabel') }}
          </div>
          <div class="my-article-watches__head-button article-watch-column__button"></div>
        </div>

        <div v-if="filteredActiveWatchesWithProducts.length > 0" class="my-article-watches__list">
          <oie-article-watch-item
            v-for="articleWatch in filteredActiveWatchesWithProducts"
            :key="articleWatch.id"
            :article-watch="articleWatch"
            :show-checkbox="hasMultiSelect"
            :is-selected="selectedItems.includes(articleWatch.id)"
            :is-super-user="isSuperUser"
            @onModalHide="getArticleWatchByUser"
            @onSelectionChange="handleItemSelection"></oie-article-watch-item>
        </div>

        <div v-else-if="currentFilteredWatches.length === 0 && currentWatches.length > 0" class="my-article-watches__no-results">
          <p>{{ tStatic('noResultsFound') }}</p>
        </div>

        <div v-else-if="currentWatches.length === 0" class="my-article-watches__empty">
          <p>{{ tStatic('noArticleWatches') }}</p>
        </div>
      </oie-accordion-tab>
      <oie-accordion-tab
        name="completed"
        :display-name="`Slutförd bevakning${filteredCompletedWatchesWithProducts.length > 0 ? ` (${filteredCompletedWatchesWithProducts.length})` : ''}`"
        :selected="activeTab === 'completed'">
        <!-- Filter and Actions Section -->
        <div v-if="currentWatches.length > 0" class="my-article-watches__controls">
          <div class="my-article-watches__filters">
            <div class="filter-group">
              <label for="status-filter-completed">{{ t('statusLabel') }}:</label>
              <select id="status-filter-completed" v-model="currentFilters.status" @change="applyFilters">
                <option value="">
                  {{ tStatic('allStatuses') }}
                </option>
                <option value="in-stock">
                  {{ tStatic('stockAvailableLabel') }}
                </option>
                <option value="few-stock">
                  {{ tStatic('stockFewLabel') }}
                </option>
                <option value="no-stock">
                  {{ tStatic('stockNoneLabel') }}
                </option>
                <option value="is-order-item">
                  {{ tStatic('stockIsOrderItemLabel') }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label for="sort-filter-completed">{{ tStatic('sortBy') }}:</label>
              <select id="sort-filter-completed" v-model="currentFilters.sortBy" @change="applyFilters">
                <option value="dateDesc">
                  {{ tStatic('dateNewestFirst') }}
                </option>
                <option value="dateAsc">
                  {{ tStatic('dateOldestFirst') }}
                </option>
                <option value="nameAsc">
                  {{ tStatic('articleNameAZ') }}
                </option>
                <option value="nameDesc">
                  {{ tStatic('articleNameZA') }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label for="search-filter-completed">{{ tStatic('searchByName') }}:</label>
              <input
                id="search-filter-completed"
                v-model="currentFilters.searchText"
                type="text"
                :placeholder="tStatic('searchPlaceholder')"
                @input="applyFilters">
            </div>
          </div>

          <div v-if="hasMultiSelect && isSuperUser" class="my-article-watches__bulk-actions">
            <div class="bulk-selection">
              <oie-base-checkbox
                v-model="selectAll"
                :text="tStatic('selectAll')"
                @change="toggleSelectAll"></oie-base-checkbox>
              <span v-if="selectedItems.length > 0" class="selection-count">
                {{ tStatic('selectedItems').replace('{count}', selectedItems.length) }}
              </span>
            </div>

            <div v-if="selectedItems.length > 0" class="bulk-actions">
              <oie-base-button
                :text="tStatic('deleteSelected')"
                css-classes="button--red"
                @click="showBulkDeleteModal">
                <template #iconPrepend>
                  <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
                </template>
              </oie-base-button>
            </div>
          </div>
        </div>

        <div class="my-article-watches__head" :class="{'my-article-watches__head--with-checkbox': hasMultiSelect && isSuperUser}">
          <div v-if="hasMultiSelect && isSuperUser" class="my-article-watches__head-checkbox"></div>
          <div class="my-article-watches__head-image article-watch-column__image"></div>
          <div class="my-article-watches__head-article article-watch-column__article">
            {{ tStatic('articleNumberLabel') }}
          </div>
          <div class="my-article-watches__head-date article-watch-column__added-date">
            {{ t('addedLabel') }}
          </div>
          <div v-if="isSuperUser" class="my-article-watches__head-sentat article-watch-column__sent-at">
            {{ t('sendAtLabel') }}
          </div>
          <div class="my-article-watches__head-price article-watch-column__price">
            {{ t('pricePerArticle') }}
          </div>
          <div class="my-article-watches__head-status article-watch-column__status">
            {{ t('statusLabel') }}
          </div>
          <div v-if="isSuperUser" class="my-article-watches__head-button article-watch-column__button"></div>
        </div>

        <div v-if="filteredCompletedWatchesWithProducts.length > 0" class="my-article-watches__list">
          <oie-article-watch-item
            v-for="articleWatch in filteredCompletedWatchesWithProducts"
            :key="articleWatch.id"
            :article-watch="articleWatch"
            :show-checkbox="hasMultiSelect && isSuperUser"
            :is-selected="selectedItems.includes(articleWatch.id)"
            :is-super-user="isSuperUser"
            @onModalHide="getArticleWatchByUser"
            @onSelectionChange="handleItemSelection"></oie-article-watch-item>
        </div>

        <div v-else-if="currentWatches.length > 0 && currentFilteredWatches.length === 0" class="my-article-watches__no-results">
          <p>{{ tStatic('noResultsFound') }}</p>
        </div>

        <div v-else-if="currentWatches.length === 0" class="my-article-watches__empty">
          <p>{{ tStatic('noArticleWatches') }}</p>
        </div>
      </oie-accordion-tab>
    </oie-accordion-tabs>
  </oie-my-pages-template>
</template>

<script>
import priceService from '@/core/price-service'
import httpService from '@/core/http-service'
import _ from 'lodash'
import tabs from '@/mixins/tabs'

export default {
  mixins: [tabs],
  data: () => ({
    articleWatches: [],
    filteredActiveWatches: [],
    filteredCompletedWatches: [],
    activeTab: 'active', // 'active' or 'completed'
    activeFilters: {
      status: '',
      sortBy: 'dateDesc',
      searchText: ''
    },
    completedFilters: {
      status: '',
      sortBy: 'dateDesc',
      searchText: ''
    },
    selectedItems: [],
    selectAll: false,
    hasMultiSelect: true,
    updatingSelectAll: false, // Flag to prevent circular updates
    isInitialLoad: true // Flag to prevent double filtering during initial load
  }),
  computed: {
    activeWatches() {
      return this.articleWatches.filter(watch => !watch.sent)
    },
    completedWatches() {
      return this.articleWatches.filter(watch => watch.sent)
    },
    currentWatches() {
      return this.activeTab === 'active' ? this.activeWatches : this.completedWatches
    },
    currentFilteredWatches() {
      return this.activeTab === 'active' ? this.filteredActiveWatchesWithProducts : this.filteredCompletedWatchesWithProducts
    },
    currentFilters() {
      return this.activeTab === 'active' ? this.activeFilters : this.completedFilters
    },
    filteredActiveWatchesWithProducts() {
      return this.filteredActiveWatches.filter(watch =>
        watch.product && watch.product.items && watch.product.items.length > 0
      )
    },
    filteredCompletedWatchesWithProducts() {
      return this.filteredCompletedWatches.filter(watch =>
        watch.product && watch.product.items && watch.product.items.length > 0
      )
    },
    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    }
  },
  watch: {
    activeTab(newTab, oldTab) {
      // Only proceed if tab actually changed
      if (newTab === oldTab) return
      // Clear selections when switching tabs
      this.selectedItems = []
      this.selectAll = false
      // Only apply filters if we have article watches loaded and it's not the initial load
      if (this.articleWatches.length > 0 && !this.isInitialLoad) {
        this.applyFilters()
      }
      // Save the new tab selection
      sessionStorage.setItem('article-watches-tab', newTab)
    },
    selectAll(newVal) {
      // Prevent circular updates when updateSelectAllState is changing selectAll
      if (this.updatingSelectAll) {
        return
      }

      // Only update selectedItems when user actually clicks the selectAll checkbox
      if (newVal && this.selectedItems.length !== this.currentFilteredWatches.length) {
        this.selectedItems = this.currentFilteredWatches.map(item => item.id)
      } else if (!newVal && this.selectedItems.length > 0) {
        this.selectedItems = []
      }
    },
    // Watch for changes in currentFilteredWatches to update selectAll state
    currentFilteredWatches(newVal) {
      // Update selectAll state when filtered list changes
      this.updateSelectAllState()
    }
  },
  mounted() {
    this.getArticleWatchByUser()
    // Initialize the tab state properly
    this.initializeTab()
    // Subscribe to tab changes from accordion-tabs component
    this.subscribe('accordion-tab-changed', (tabName) => {
      // Only change tab if it's different to prevent double filtering
      if (this.activeTab !== tabName) {
        this.activeTab = tabName
        // Save the selected tab for next visit
        sessionStorage.setItem('article-watches-tab', tabName)
        // Note: applyFilters will be called by the activeTab watcher
      }
    })
  },
  beforeDestroy() {
    // Clean up subscriptions when component is destroyed
    this.unsubscribe('accordion-tab-changed')
  },
  methods: {
    initializeTab() {
      // Get the remembered tab or default to 'active'
      const rememberedTab = sessionStorage.getItem('article-watches-tab') || 'active'
      this.activeTab = rememberedTab
      // Reset initial load flag when component is mounted
      this.isInitialLoad = true
      // Ensure accordion tabs component knows about the correct initial state
      this.$nextTick(() => {
        if (this.$refs.tabs && this.$refs.tabs.setActiveTab) {
          this.$refs.tabs.setActiveTab(rememberedTab)
        }
      })
    },
    getArticleWatchByUser() {
      httpService.get('article-watch/user').then(res => {
        const articleWatches = res.data.items.map(articleWatch => {
          const { id, articleNumber, createdAt, sent, sentAt, email } = articleWatch
          return { id, articleNumber, createdAt, sent, sentAt, email }
        })

        this.getProducts(articleWatches)
      })
    },
    getProducts(articleWatches) {
      const ids = articleWatches.map(a => {
        return a.articleNumber
      })

      httpService.post('products', { productIds: ids, page: 1, pageSize: 1 }).then(res => {
        this.getProductListPrices(res.data.data, articleWatches)
      }, () => { this.notValid = true })
    },
    getProductListPrices(productData, articleWatches) {
      const ids = productData.map(p => {
        return p.id
      })
      httpService.post('productListPrices', { productId: ids }).then(res => {
        let products = productData
        priceService.populatePricesFromBackend(res.data, products)
        const newArticleWatches = articleWatches

        _.forEach(products, p => {
          const product = p
          const items = p.items
          _.forEach(items, item => {
            const index = _.findIndex(newArticleWatches, a => a.articleNumber === item.id)
            if (index !== -1) {
              product.items = [item]
              newArticleWatches[index].product = product
              // Add stock status for filtering
              newArticleWatches[index].stockStatus = this.getStockStatus(item)
              return false
            }
          })
        })

        this.articleWatches = newArticleWatches
        this.applyFilters()
        // Mark initial load as complete
        this.isInitialLoad = false
      })
    },
    getStockStatus(item) {
      const number = item.stock || 0
      const threshold = this.getProductFewInStock() || 0

      if (number >= threshold) {
        return 'in-stock'
      } else if (number > 0) {
        return 'few-stock'
      } else if (item.isOrderItem) {
        return 'is-order-item'
      } else {
        return 'no-stock'
      }
    },
    getProductFewInStock() {
    // You might need to implement this method based on your business logic
      return 5 // Default threshold
    },
    applyFilters() {
      let activeFiltered = [...this.activeWatches]
      let completedFiltered = [...this.completedWatches]

      // Function to apply filters to a list
      const applyFiltersToList = (filtered, filters) => {
        // Filter by status
        if (filters.status) {
          filtered = filtered.filter(item => item.stockStatus === filters.status)
        }

        // Filter by search text
        if (filters.searchText) {
          const searchLower = filters.searchText.toLowerCase()
          filtered = filtered.filter(item => {
            // Check if product and product name exist
            const hasProductName = item.product && item.product.name
            const hasProductId = item.product && item.product.items && item.product.items.length > 0 && item.product.items[0].id

            if (!hasProductName && !hasProductId) {
              return false // Skip items without searchable data
            }

            // Search in product name if it exists
            const nameMatch = hasProductName && item.product.name.toLowerCase().includes(searchLower)

            // Search in product ID if it exists
            const idMatch = hasProductId && item.product.items[0].id.toLowerCase().includes(searchLower)

            return nameMatch || idMatch
          })
        }

        // Sort
        switch (filters.sortBy) {
          case 'dateDesc':
            filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            break
          case 'dateAsc':
            filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            break
          case 'nameAsc':
            filtered.sort((a, b) => a.product.name.localeCompare(b.product.name))
            break
          case 'nameDesc':
            filtered.sort((a, b) => b.product.name.localeCompare(a.product.name))
            break
        }

        return filtered
      }

      this.filteredActiveWatches = applyFiltersToList(activeFiltered, this.activeFilters)
      this.filteredCompletedWatches = applyFiltersToList(completedFiltered, this.completedFilters)

      this.selectedItems = []
      this.selectAll = false
      this.updateSelectAllState()
    },
    handleItemSelection(data) {
      const { id, isSelected } = data
      if (isSelected) {
        if (!this.selectedItems.includes(id)) {
          this.selectedItems.push(id)
        }
      } else {
        this.selectedItems = this.selectedItems.filter(itemId => itemId !== id)
      }

      // Update selectAll state
      this.updateSelectAllState()
    },
    updateSelectAllState() {
    // Use flag to prevent circular updates
      this.updatingSelectAll = true

      // Determine if selectAll should be checked
      const shouldBeChecked = this.selectedItems.length === this.currentFilteredWatches.length && this.currentFilteredWatches.length > 0

      if (this.selectAll !== shouldBeChecked) {
        this.selectAll = shouldBeChecked
      }

      // Reset flag on next tick to allow user interactions
      this.$nextTick(() => {
        this.updatingSelectAll = false
      })
    },
    toggleSelectAll() {
    // The watch handler will take care of updating selectedItems
    },
    showBulkDeleteModal() {
      this.publish('modal.show.bulk-delete-article-watches', {
        ids: this.selectedItems,
        onHide: this.onBulkDeleteComplete
      })
    },
    onBulkDeleteComplete() {
      this.selectedItems = []
      this.selectAll = false
      this.getArticleWatchByUser()
    }
  }
}
</script>

<style lang="scss">
.my-article-watches__controls {
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid color(gray-4);
  border-radius: 4px;
  background-color: color(background-white);
}

.my-article-watches__filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @include media(medium) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    @include body-smaller-semi-bold;
    color: color(gray-2);
  }

  select,
  input {
    padding: 0.5rem;
    border: 1px solid color(gray-4);
    border-radius: 4px;
    @include body-smaller;

    &:focus {
      outline: none;
      border-color: color(primary);
    }
  }
}

.my-article-watches__bulk-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid color(gray-4);

  @include media(to-medium) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.bulk-selection {
  display: flex;
  align-items: center;
  gap: 1rem;

  .selection-count {
    @include body-smaller;
    color: color(gray-2);
  }
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
}

.my-article-watches__no-results {
  text-align: center;
  padding: 2rem;
  color: color(gray-2);
}

.my-article-watches__empty {
  text-align: center;
  padding: 2rem;
  color: color(gray-2);
}

.my-article-watches__head {
  display: none;
  background-color: color(background-blue-6);
  padding: 1.125rem 0.75rem 0.75rem 0.75rem;
  border: 1px solid color(gray-4);
  @include body-smaller-semi-bold;
  color: color(gray-2);
  margin-bottom: 0.5rem;
}

.my-article-watches__list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

@include media(x-small) {
  .my-article-watches__list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@include media(medium) {
  .article-watch-list {
    > h1 {
      @include h1-semi-bold;
    }
  }
  .my-article-watches__head {
    display: flex;
    gap: 0.5rem;
  }

  .my-article-watches__list {
    grid-template-columns: 1fr;
  }

  .my-article-watches__head,
  .article-watch-item {
    .article-watch-column__checkbox {
      width: 1.25rem; // Checkbox (more compact)
    }
    .article-watch-column__image {
      width: 10%;
    }
    .article-watch-column__article {
      width: 28%;
    }
    .article-watch-column__added-date {
      width: 10%;
    }
    .article-watch-column__sent-at {
      width: 10%;
    }
    .article-watch-column__price {
      width: 10%;
    }
    .article-watch-column__status {
      width: 16%;
    }
    .article-watch-column__button {
      width: 4.375rem;
      margin-left: auto;
    }
  }
  .article-watch-item:not(:has(.article-watch-column__sent-at)),
  .my-article-watches__head:not(:has(.article-watch-column__sent-at)) {
    .article-watch-column__status {
      width: 26%;
    }
  }
}
</style>
